import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useUI } from 'store/UI/selectors';
import { useCurrentUser } from 'store/users/selectors';
import { handleGenerateTranscript } from 'store/interactions/thunks';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { downloadAudioFile } from 'utils/methods/downloadAudioFile';
import useBadWords from '../words/badWords';
import { apiPost } from '../apiCalling';
import { handleCheckUserExist } from 'store/users/thunks';

const usePermissionForUser = () => {
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const { orgPermission = {} } = useUI();
  const showAlert = useAlert();
  const { getTextFromHtml } = useBadWords();

  const isDistrictAdmin = useMemo(() => {
    if (currentUser?.role === 'admin') {
      return true;
    }
    return false;
  }, [currentUser]);

  const isUserCoach = useMemo(() => {
    if (currentUser?.role === 'admin' && currentUser?.user_sub_roles === 'coach') {
      return true;
    }
    return false;
  }, [currentUser]);

  const districtPermissionConfig = useMemo(() => {
    if (currentUser?.role === 'teacher') {
      if (orgPermission?.config) {
        return orgPermission?.config;
      } else {
        return {};
      }
    } else {
      return {};
    }
  }, [orgPermission]);

  const downloadAudio = async ({ audioUrl }) => {
    try {
      const result = await downloadAudioFile(audioUrl);
      if (result.fallback) {
        showAlert(
          'Opened audio in a new tab. Use your browser to save the file.',
          'info',
        );
      } else {
        showAlert('Audio downloaded successfully', 'success');
      }
    } catch (error) {
      showAlert('Failed to download audio', 'error');
    }
  };

  //create a function for download transcript text
  const downloadTranscriptText = async ({ text }) => {
    try {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      const cleanedText = text.replace(/Generating images\.\.\./g, '').trim();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      
      const addWatermark = () => {
        doc.setGState(new doc.GState({ opacity: 0.05 }));
        doc.setFontSize(48);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(200, 200, 200);
        
        const watermarkText = 'One Way Education';
        const textWidth = doc.getTextWidth(watermarkText);
        const textHeight = 12;
        
        const centerX = (pageWidth - textWidth) / 2;
        const centerY = pageHeight / 2;
        
        doc.text(watermarkText, centerX, centerY);
        doc.setGState(new doc.GState({ opacity: 1 }));
      };
      
      addWatermark();
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      
      const lines = doc.splitTextToSize(cleanedText, maxWidth);
      let yPosition = margin;
      let lineCounter = 0;
      
      lines.forEach((line) => {
        if (yPosition > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          addWatermark();
          doc.setFontSize(12);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
          yPosition = margin;
          lineCounter = 0;
        }
        doc.text(line, margin, yPosition);
        yPosition += 7;
        lineCounter++;
        
        if (lineCounter % 4 === 0) {
          yPosition += 7;
        }
      });
      
      const fileName = `transcript_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.log(error);
      showAlert('Failed to download transcript', 'error');
    }
  };

  const downloadTranscipt = async ({ audioUrl, paragraph }) => {
    try {
      if (paragraph?.length > 0) {
        const text = getTextFromHtml(paragraph?.join(''));
        await downloadTranscriptText({ text });
        showAlert('Transcript generated successfully and downloaded', 'success');
        return;
      }
      const response = await apiPost('/soundScript/generate-sound-script', { audioUrl });
      if (response?.statusCode === 200) {
        const text = response?.text;
        if (text) {
          await downloadTranscriptText({ text });
          showAlert('Transcript generated successfully and downloaded', 'success');
        } else {
          showAlert('Transcript Audio is not available', 'error');
        }
      } else {
        showAlert('Failed to generate transcript audio', 'error');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const checkUserExist = async (email) => {
    try {
      if(!email) return false;
      const response = await dispatch(handleCheckUserExist(email));
      if (response?.status_code === 200) {
        return response?.data;
      } else {
        return false;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  return {
    isUserCoach,
    isDistrictAdmin,
    districtPermissionConfig,
    downloadTranscipt,
    downloadTranscriptText,
    downloadAudio,
    checkUserExist
  };
};

export default usePermissionForUser;
